import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import type { AuthResult } from '@/components/auth/types';
import { CAMPAIGN_SESSION_STORAGE_KEY } from '@/constants';
import { useCurrentUserContext } from '@/contexts';
import { ProfileFragment } from '@/fragments';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { useRevalidate } from '../useAuthenticate/useRevalidate';
import type {
  RequestEmailLoginCodeMutation,
  RequestEmailLoginCodeMutationVariables,
  VerifyEmailLoginCodeMutation,
  VerifyEmailLoginCodeMutationVariables,
} from './useEmailLoginCode.generated';

const requestQuery = gql`
  mutation RequestEmailLoginCode($email: LowercaseString!) {
    requestEmailLoginCode(email: $email)
  }
`;

const verifyQuery = gql`
  ${ProfileFragment}
  mutation VerifyEmailLoginCode(
    $email: LowercaseString!
    $code: String!
    $name: String
    $timeZone: TimeZone
    $campaign: String
  ) {
    verifyEmailLoginCode(
      email: $email
      code: $code
      name: $name
      timeZone: $timeZone
      campaign: $campaign
    ) {
      isNewUser
      user {
        ...Profile
      }
    }
  }
`;

type VerifyValues = {
  email: string;
  code: string;
  name?: string;
};

/**
 * Deliberately a sibling of `useAuthenticate` rather than another branch inside
 * it: that hook is shaped around a password, and threading a passwordless flow
 * through it would make every one of its values conditionally meaningful.
 */
export const useEmailLoginCode = () => {
  const { revalidate } = useRevalidate();
  const { setUser } = useCurrentUserContext();

  const {
    mutateAsync: mutateRequest,
    isPending: isRequesting,
    error: requestError,
    reset: resetRequest,
  } = useMutation<
    RequestEmailLoginCodeMutation,
    QueryError,
    RequestEmailLoginCodeMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        RequestEmailLoginCodeMutation,
        RequestEmailLoginCodeMutationVariables
      >(requestQuery, variables),
  });

  const {
    mutate: mutateVerify,
    isPending: isVerifying,
    data: verifyData,
    error: verifyError,
    reset: resetVerify,
  } = useMutation<
    VerifyEmailLoginCodeMutation,
    QueryError,
    VerifyEmailLoginCodeMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        VerifyEmailLoginCodeMutation,
        VerifyEmailLoginCodeMutationVariables
      >(verifyQuery, variables),
  });

  /**
   * Resolves when the code has actually been sent, and rejects if it has not.
   *
   * Awaitable rather than callback-based because moving to the code step resets
   * this mutation, which drops any `onSuccess` handed to `mutate` - so the caller
   * cannot otherwise tell a delivered code from a failed one.
   */
  const requestCode = (email: string) => mutateRequest({ email });

  const verifyCode = (
    { email, code, name }: VerifyValues,
    options?: MutateOptions<
      VerifyEmailLoginCodeMutation,
      QueryError,
      VerifyEmailLoginCodeMutationVariables
    >
  ) => {
    const timeZone = DateTime.local().zoneName;
    const campaign = sessionStorage.getItem(CAMPAIGN_SESSION_STORAGE_KEY);

    mutateVerify(
      { email, code, name, timeZone, campaign },
      {
        ...options,
        onSuccess: (data, ...args) => {
          revalidate();
          setUser(data.verifyEmailLoginCode.user);
          options?.onSuccess?.(data, ...args);
        },
      }
    );
  };

  // Stable identity: AuthPanel lists this in an effect's dependencies, and a new
  // function each render would re-run that effect on every render, which resets
  // the form and re-renders - an infinite loop.
  const reset = useCallback(() => {
    resetRequest();
    resetVerify();
  }, [resetRequest, resetVerify]);

  // `isNewUser` lets us report the existing 'login' | 'register' result honestly,
  // so no consumer of AuthResult has to change.
  const result: AuthResult | null = verifyData
    ? verifyData.verifyEmailLoginCode.isNewUser
      ? 'register'
      : 'login'
    : null;

  return {
    requestCode,
    verifyCode,
    isRequesting,
    isVerifying,
    data: verifyData,
    error: verifyError || requestError,
    result,
    reset,
  };
};
